/**
 * Starts and tears down a Cost Price practice session.
 *
 * `practiceGuards.ts` owns the in-memory copy and the write interception; this module is
 * the only part allowed to touch Zustand, and it does two things:
 *   1. seeds the practice dish / order / subrecipes into the stores the real editor reads,
 *   2. keeps them there for as long as the branch runs, re-asserting after any refetch.
 *
 * Seeding is deliberately additive and reversible. Nothing the user owns is mutated: the
 * teardown removes rows by practice id, so a restore cannot clobber a real row that
 * arrived while the tour was open.
 */
import { useAppUserDefaultStore, isAllRestaurantsSelected } from '../../../stores/appUserDefaultStore';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useCostPriceStore, type CostPriceActiveTab } from '../../../stores/appCostPriceStore';
import { useOrderHistory } from '../../../stores/appOrderHistoryStore';
import { useSubrecipesStore } from '../../../stores/appSubrecipesStore';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { buildPracticeDish, buildPracticeExampleDishes, buildPracticeOrder } from './costPriceFixture';
import {
  PRACTICE_ORDER_ID,
  beginPracticeData,
  endPracticeData,
  isPracticeActive,
  isPracticeId,
  onPracticeDataChange,
  practiceDish,
  practiceDishes,
  practiceSubrecipes,
} from './practiceGuards';

let unsubscribers: (() => void)[] = [];

/**
 * Everything the user may have left switched on that would hide the fixture: the food /
 * drinks tab, the menu filter, the picker's supplier and category filters, plus the page's
 * own category selector and the table's search box, which are component state and are
 * cleared by an event instead. All of it is put back on teardown.
 */
interface FilterSnapshot {
  activeTab: CostPriceActiveTab;
  menuFilters: string[];
  /** The practice dish has no menu-engineering class, so any matrix filter hides it. */
  matrixFilters: string[];
  supplierFilters: string[];
  categoryFilters: string[];
  /** Component state, so it is asked for rather than read. */
  local: { categoryId?: string; search?: string };
}
let filterSnapshot: FilterSnapshot | null = null;

/**
 * Ensure a single restaurant is selected, because the whole Cost Price page early-returns
 * a "pick a restaurant" notice under All Restaurants. `setRestaurant` is session state, so
 * this does not change what the user sees on their next visit.
 */
export function ensurePracticeRestaurant(): string | null {
  const defaults = useAppUserDefaultStore.getState();
  if (defaults.restaurant && !isAllRestaurantsSelected(defaults.restaurant)) return defaults.restaurant.id;
  const first = useRestaurantStore.getState().restaurants[0];
  if (!first) return null;
  defaults.setRestaurant(first);
  return first.id;
}

/**
 * Put the practice rows into the stores, and do NOTHING when they are already there.
 *
 * The no-op path matters: this runs from store subscriptions, so writing unconditionally
 * would retrigger itself forever. Identity, not deep equality, is the test — the guards
 * hand out a fresh object on every edit, so a stale reference means a refetch replaced it.
 */
function seedStores(restaurantId: string) {
  const dishes = practiceDishes();
  const current = useCostPriceStore.getState().costPriceList;
  if (current.filter(analysis => isPracticeId(analysis.id)).length !== dishes.length
    || dishes.some(dish => current.find(analysis => analysis.id === dish.id) !== dish)) {
    useCostPriceStore.setState({
      costPriceList: [...dishes, ...current.filter(analysis => !isPracticeId(analysis.id))],
    });
  }

  const subrecipes = practiceSubrecipes();
  if (subrecipes.length > 0) {
    const state = useSubrecipesStore.getState();
    if (subrecipes.some(subrecipe => state.subrecipesById.get(subrecipe.id) !== subrecipe)) {
      const byId = new Map(state.subrecipesById);
      subrecipes.forEach(subrecipe => byId.set(subrecipe.id, subrecipe));
      useSubrecipesStore.setState({
        subrecipesById: byId,
        subrecipesList: [
          ...subrecipes,
          ...state.subrecipesList.filter(subrecipe => !isPracticeId(subrecipe.id)),
        ],
      });
    }
  }

  // The picker builds its From Orders list straight from this store, so the fixture
  // invoice has to be present for the walkthrough's products to be findable.
  const orders = useOrderHistory.getState().orderHistoryList;
  if (!orders.some(order => order.id === PRACTICE_ORDER_ID)) {
    useOrderHistory.setState({ orderHistoryList: [buildPracticeOrder(restaurantId), ...orders] });
  }
}

function clearStores() {
  useCostPriceStore.setState(state => ({
    costPriceList: state.costPriceList.filter(analysis => !isPracticeId(analysis.id)),
  }));
  useSubrecipesStore.setState(state => {
    const byId = new Map(state.subrecipesById);
    Array.from(byId.keys()).forEach(id => { if (isPracticeId(id)) byId.delete(id); });
    return {
      subrecipesById: byId,
      subrecipesList: state.subrecipesList.filter(subrecipe => !isPracticeId(subrecipe.id)),
    };
  });
  useOrderHistory.setState(state => ({
    orderHistoryList: state.orderHistoryList.filter(order => !isPracticeId(order.id)),
  }));
}

/**
 * Begin a practice branch. Returns false when there is no restaurant to run it against,
 * which is the caller's cue to skip the branch rather than show an editor over real data.
 */
export function startPractice(overview = false, restart = false): boolean {
  if (isPracticeActive()) {
    // A branch starting on top of a session that already has work in it would replay its
    // steps over a dish that already holds them, and recreating the sauce would trip the
    // duplicate-name check. Only an untouched session is safe to reuse.
    const dish = practiceDish();
    const untouched = dish !== null && dish.ingredients.length === 0 && practiceSubrecipes().length === 0;
    if (untouched && !restart && !overview) return true;
    unsubscribers.forEach(unsubscribe => unsubscribe());
    unsubscribers = [];
    endPracticeData();
    clearStores();
  }
  const restaurantId = ensurePracticeRestaurant();
  if (!restaurantId) return false;

  beginPracticeData(restaurantId, buildPracticeDish(restaurantId, overview), buildPracticeExampleDishes(restaurantId));

  // A branch that opens onto a filtered table or a filtered picker shows the user an
  // empty screen and a step pointing at nothing, so every filter is neutralised first.
  const costPriceState = useCostPriceStore.getState();
  const filtersState = useFiltersStore.getState();
  if (!filterSnapshot) {
    filterSnapshot = {
      activeTab: costPriceState.activeTab,
      menuFilters: filtersState.costPriceMenuFilters,
      matrixFilters: filtersState.matrixFilters,
      supplierFilters: costPriceState.addIngredientSupplierFilters,
      categoryFilters: costPriceState.addIngredientCategoryFilters,
      local: {},
    };
    const local: { categoryId?: string; search?: string } = {};
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('onboarding:cost-snapshot-filters', { detail: local }));
    }
    filterSnapshot.local = local;
  }
  if (costPriceState.activeTab !== 'all') costPriceState.setActiveTab('all');
  filtersState.setCostPriceMenuFilters(['__all__']);
  filtersState.setMatrixFilters([]);
  costPriceState.setAddIngredientSupplierFilters([]);
  costPriceState.setAddIngredientCategoryFilters([]);
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('onboarding:cost-close-dish'));
    window.dispatchEvent(new CustomEvent('onboarding:cost-reset-filters'));
  }

  seedStores(restaurantId);

  unsubscribers = [
    onPracticeDataChange(() => seedStores(restaurantId)),
    // A refetch triggered by the editor itself (an ingredient add reloads the whole list)
    // lands here; re-seed rather than let the practice rows disappear mid-step.
    useCostPriceStore.subscribe(() => { if (isPracticeActive()) seedStores(restaurantId); }),
    useSubrecipesStore.subscribe(() => { if (isPracticeActive()) seedStores(restaurantId); }),
    useOrderHistory.subscribe(() => { if (isPracticeActive()) seedStores(restaurantId); }),
  ];
  return true;
}

/** End the branch and take every practice row back out of the stores. Safe to call twice. */
export function endPractice(): void {
  if (!isPracticeActive()) return;
  unsubscribers.forEach(unsubscribe => unsubscribe());
  unsubscribers = [];
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('onboarding:cost-close-dish'));
  endPracticeData();
  clearStores();
  if (filterSnapshot) {
    const costPriceState = useCostPriceStore.getState();
    if (filterSnapshot.activeTab !== 'all') costPriceState.setActiveTab(filterSnapshot.activeTab);
    costPriceState.setAddIngredientSupplierFilters(filterSnapshot.supplierFilters);
    costPriceState.setAddIngredientCategoryFilters(filterSnapshot.categoryFilters);
    useFiltersStore.getState().setCostPriceMenuFilters(filterSnapshot.menuFilters);
    useFiltersStore.getState().setMatrixFilters(filterSnapshot.matrixFilters);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('onboarding:cost-restore-filters', { detail: filterSnapshot.local }));
    }
  }
  filterSnapshot = null;
}
